/**
 * ============================================================================
 * App监控日志 模块 API 路由定义
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义 App 监控日志相关 API 的路由常量
 * - 所有路由使用函数或常量形式，便于类型安全调用
 * 
 * ⚠️ 注意：
 * - 此路由对应后端 AppLogController、AppMonitoringAlertController、AppFunnelController
 * - 批量上传接口（batch）为免鉴权接口，用于 App 端上报
 * - 查询接口需要鉴权，用于 Web 管理后台
 * 
 * @module api/app-logs/routes
 * @author YSFL Team
 * @created 2026-01-21
 * @updated 2026-01-31 同步后端所有路由定义
 * ============================================================================
 */

import { API_V1_ROUTES } from '../routes/v1'

/** App 日志 API 前缀（用于路径匹配） */
export const APP_LOGS_PREFIX = API_V1_ROUTES.appLog.prefix

export const APP_LOGS_ROUTES = {
  /** API 前缀（用于路径匹配检测） */
  prefix: APP_LOGS_PREFIX,

  // ========== 性能日志 ==========
  performance: {
    /** 批量上传性能日志 POST /api/v1/app-logs/performance/batch（免鉴权） */
    batch: API_V1_ROUTES.appLog.batch,
    /** 查询性能日志列表 GET /api/v1/app-logs/performance（需鉴权） */
    list: API_V1_ROUTES.appLog.list,
    /** 性能统计聚合 GET /api/v1/app-logs/performance/statistics（需鉴权） */
    statistics: API_V1_ROUTES.appLog.statistics,
    /** 扫码阶段统计 GET /api/v1/app-logs/performance/scanner-statistics（需鉴权） */
    scannerStatistics: API_V1_ROUTES.appLog.scannerStatistics,
  },

  // ========== 错误日志 ==========
  errors: {
    /** 批量上传错误日志 POST /api/v1/app-logs/errors/batch（免鉴权） */
    batch: API_V1_ROUTES.appLog.errorsBatch,
    /** 查询错误日志列表 GET /api/v1/app-logs/errors（需鉴权） */
    list: API_V1_ROUTES.appLog.errors,
    /** 错误统计聚合 GET /api/v1/app-logs/errors/statistics（需鉴权） */
    statistics: API_V1_ROUTES.appLog.errorsStatistics,
  },

  // ========== 用户行为日志 ==========
  journey: {
    /** 批量上传行为日志 POST /api/v1/app-logs/journey/batch（免鉴权） */
    batch: API_V1_ROUTES.appLog.journeyBatch,
    /** 查询行为日志列表 GET /api/v1/app-logs/journey（需鉴权） */
    list: API_V1_ROUTES.appLog.journey,
    /** 行为统计聚合 GET /api/v1/app-logs/journey/statistics（需鉴权） */
    statistics: API_V1_ROUTES.appLog.journeyStatistics,
  },

  // ========== API 分析 ==========
  api: {
    /** App 日志上报健康状态 GET /api/v1/app-logs/health（需鉴权） */
    health: API_V1_ROUTES.appLog.health,
    /** 排障追踪 GET /api/v1/app-logs/traces（需鉴权） */
    traces: API_V1_ROUTES.appLog.traces,
    /** API 统计分析（全局/用户/设备维度） GET /api/v1/app-logs/api/statistics（需鉴权） */
    statistics: API_V1_ROUTES.appLog.apiStatistics,
    /** 单个 API 详情分析 GET /api/v1/app-logs/api/detail（需鉴权） */
    detail: API_V1_ROUTES.appLog.apiDetail,
  },

  // ========== 用户分析 ==========
  users: {
    /** 用户列表 GET /api/v1/app-logs/users（需鉴权） */
    list: API_V1_ROUTES.appLog.users,
    /** 用户画像 GET /api/v1/app-logs/users/:userId/profile（需鉴权） */
    profile: API_V1_ROUTES.appLog.usersProfile,
    /** 用户时间线 GET /api/v1/app-logs/users/:userId/timeline（需鉴权） */
    timeline: API_V1_ROUTES.appLog.usersTimeline,
  },

  // ========== 设备分析 ==========
  devices: {
    /** 设备列表 GET /api/v1/app-logs/devices（需鉴权） */
    list: API_V1_ROUTES.appLog.devices,
    /** 设备画像 GET /api/v1/app-logs/devices/:deviceId/profile（需鉴权） */
    profile: API_V1_ROUTES.appLog.devicesProfile,
    /** 设备时间线 GET /api/v1/app-logs/devices/:deviceId/timeline（需鉴权） */
    timeline: API_V1_ROUTES.appLog.devicesTimeline,
  },

  // ========== 告警管理 ==========
  alerts: {
    /** 告警列表 GET /api/v1/app-logs/alerts（需鉴权） */
    list: API_V1_ROUTES.appLog.alerts,
    /** 告警统计概览 GET /api/v1/app-logs/alerts/summary（需鉴权） */
    summary: API_V1_ROUTES.appLog.summary,
    /** 告警详情 GET /api/v1/app-logs/alerts/:id（需鉴权） */
    detail: API_V1_ROUTES.appLog.alertDetail,
    /** 处理告警 POST /api/v1/app-logs/alerts/:id/handle（需鉴权） */
    handle: API_V1_ROUTES.appLog.handleAlert,
    /** 批量处理告警 POST /api/v1/app-logs/alerts/batch-handle（需鉴权） */
    batchHandle: API_V1_ROUTES.appLog.batchHandle,
    /** 手动触发检测 POST /api/v1/app-logs/alerts/detect（需鉴权） */
    detect: API_V1_ROUTES.appLog.detect,
  },

  // ========== 漏斗分析 ==========
  funnel: {
    /** 漏斗定义列表 GET /api/v1/app-logs/funnel/list（需鉴权） */
    list: API_V1_ROUTES.appLog.funnelList,
    /** 漏斗分析数据 GET /api/v1/app-logs/funnel/analysis（需鉴权） */
    analysis: API_V1_ROUTES.appLog.funnelAnalysis,
    /** 操作中断分析 GET /api/v1/app-logs/funnel/interruptions（需鉴权） */
    interruptions: API_V1_ROUTES.appLog.funnelInterruptions,
    /** 创建漏斗定义 POST /api/v1/app-logs/funnel（需鉴权） */
    create: API_V1_ROUTES.appLog.funnel,
    /** 更新漏斗定义 PUT /api/v1/app-logs/funnel/:id（需鉴权） */
    update: API_V1_ROUTES.appLog.appLogsFunnel,
    /** 删除漏斗定义 DELETE /api/v1/app-logs/funnel/:id（需鉴权） */
    delete: API_V1_ROUTES.appLog.delete,
  },
} as const

export type AppLogsRoutes = typeof APP_LOGS_ROUTES
